import type { ParsedEstimate, RoomMapping, SupplementItem } from "./lineItemCompare";
import type { PdfHighlight } from "./pdf/types";

/**
 * Session storage contract for the workflow:
 * Upload -> Recap Variance -> Identify -> Packet
 *
 * We keep it deliberately simple: sessionStorage keys, JSON blobs.
 * (Swap to your backend persistence later without changing page logic.)
 */

const PREFIX = "ClaimSupplementAI:";

function canUseSessionStorage(): boolean {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function getKey(key: string): string {
  return `${PREFIX}${key}`;
}

function readJson<T>(key: string, fallback: T): T {
  if (!canUseSessionStorage()) return fallback;
  const raw = window.sessionStorage.getItem(getKey(key));
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(getKey(key), JSON.stringify(value));
}

function removeKey(key: string): void {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(getKey(key));
}

function readLocalJson<T>(key: string, fallback: T): T {
  if (!canUseLocalStorage()) return fallback;
  const raw = window.localStorage.getItem(getKey(key));
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeLocalJson(key: string, value: unknown): void {
  if (!canUseLocalStorage()) return;
  window.localStorage.setItem(getKey(key), JSON.stringify(value));
}

// --- Storage keys (v1) ---
const K = {
  insuredPdfUrl: "insuredPdfUrl.v1",
  carrierPdfUrl: "carrierPdfUrl.v1",

  insuredEstimate: "insuredEstimate.v1",
  carrierEstimate: "carrierEstimate.v1",

  roomMappings: "roomMappings.v1",
  claimBasics: "claimBasics.v1",

  // pairId -> selectedRowIds[]
  comparisonSelections: "comparisonSelections.v1",
  activeComparisonPairId: "activeComparisonPairId.v1",

  supplementItems: "supplementItems.v1",

  // pdf highlights for export (Identify step)
  pdfHighlights: "pdfHighlights.v1",
  annotatedPdfAttachments: "annotatedPdfAttachments.v1",
  incurredReceipts: "incurredReceipts.v1",
  tradeRecapPdf: "tradeRecapPdf.v1",
  lastRecapVariancePath: "lastRecapVariancePath.v1",
  carrierOnLeft: "carrierOnLeft.v1",
  contentsRooms: "contentsRooms.v1",
} as const;

export type ClaimBasicsSnapshot = {
  claimId?: string;
  policyNumber?: string;
  policyholderName?: string;
  propertyAddress?: string;
  carrierName?: string;
  dateOfLoss?: string;
  typeOfLoss?: string;
  deductible?: string;
  enableOcr?: boolean;
  useAiParse?: boolean;
};

export type AnnotatedPdfAttachment = {
  id: string;
  side: "insured" | "carrier";
  name: string;
  dataUrl: string;
  createdAt: number;
};

export type IncurredReceiptAttachment = {
  id: string;
  name: string;
  dataUrl: string;
  contentType?: string;
  room?: string;
  notes?: string;
  createdAt: number;
};

export type TradeRecapPdfAttachment = {
  id: string;
  name: string;
  dataUrl: string;
  createdAt: number;
};

export const claimSession = {
  // PDFs
  getInsuredPdfUrl(): string | null {
    return readJson<string | null>(K.insuredPdfUrl, null);
  },
  setInsuredPdfUrl(url: string | null): void {
    if (url === null) removeKey(K.insuredPdfUrl);
    else writeJson(K.insuredPdfUrl, url);
  },

  getCarrierPdfUrl(): string | null {
    return readJson<string | null>(K.carrierPdfUrl, null);
  },
  setCarrierPdfUrl(url: string | null): void {
    if (url === null) removeKey(K.carrierPdfUrl);
    else writeJson(K.carrierPdfUrl, url);
  },

  // Parsed estimates (JSON produced by your PDF parser)
  getInsuredEstimate(): ParsedEstimate | null {
    return readJson<ParsedEstimate | null>(K.insuredEstimate, null);
  },
  setInsuredEstimate(est: ParsedEstimate | null): void {
    if (est === null) removeKey(K.insuredEstimate);
    else writeJson(K.insuredEstimate, est);
  },

  getCarrierEstimate(): ParsedEstimate | null {
    return readJson<ParsedEstimate | null>(K.carrierEstimate, null);
  },
  setCarrierEstimate(est: ParsedEstimate | null): void {
    if (est === null) removeKey(K.carrierEstimate);
    else writeJson(K.carrierEstimate, est);
  },

  // Claim intake
  getClaimBasics(): ClaimBasicsSnapshot | null {
    return readJson<ClaimBasicsSnapshot | null>(K.claimBasics, null);
  },
  setClaimBasics(basics: ClaimBasicsSnapshot | null): void {
    if (!basics) removeKey(K.claimBasics);
    else writeJson(K.claimBasics, basics);
  },

  // Layout preference
  getCarrierOnLeft(): boolean {
    const stored = readLocalJson<boolean | null>(K.carrierOnLeft, null);
    if (stored !== null) return stored;
    const legacy = readJson<boolean | null>(K.carrierOnLeft, null);
    if (legacy !== null) {
      writeLocalJson(K.carrierOnLeft, legacy);
      return legacy;
    }
    return false;
  },
  setCarrierOnLeft(value: boolean): void {
    writeLocalJson(K.carrierOnLeft, value);
  },

  // Contents inventory room list
  getContentsRooms(): string[] {
    return readJson<string[]>(K.contentsRooms, []);
  },
  setContentsRooms(rooms: string[]): void {
    if (!rooms || rooms.length === 0) removeKey(K.contentsRooms);
    else writeJson(K.contentsRooms, rooms);
  },

  // Room mappings
  getRoomMappings(): RoomMapping[] {
    return readJson<RoomMapping[]>(K.roomMappings, []);
  },
  setRoomMappings(mappings: RoomMapping[]): void {
    writeJson(K.roomMappings, mappings);
  },

  // Comparison selections
  getComparisonSelections(): Record<string, string[]> {
    return readJson<Record<string, string[]>>(K.comparisonSelections, {});
  },
  setComparisonSelections(sel: Record<string, string[]>): void {
    writeJson(K.comparisonSelections, sel);
  },
  getActiveComparisonPairId(): string | null {
    return readJson<string | null>(K.activeComparisonPairId, null);
  },
  setActiveComparisonPairId(pairId: string | null): void {
    if (pairId === null) removeKey(K.activeComparisonPairId);
    else writeJson(K.activeComparisonPairId, pairId);
  },

  // Packet items
  getSupplementItems(): SupplementItem[] {
    return readJson<SupplementItem[]>(K.supplementItems, []);
  },
  setSupplementItems(items: SupplementItem[]): void {
    writeJson(K.supplementItems, items);
  },

  // PDF highlight annotations (Identify step)
  getPdfHighlights(): PdfHighlight[] {
    return readJson<PdfHighlight[]>(K.pdfHighlights, []);
  },
  setPdfHighlights(highlights: PdfHighlight[]): void {
    writeJson(K.pdfHighlights, highlights);
  },
  upsertPdfHighlight(highlight: PdfHighlight): void {
    const all = claimSession.getPdfHighlights();
    const idx = all.findIndex((h) => h.id === highlight.id);
    if (idx >= 0) all[idx] = highlight;
    else all.push(highlight);
    claimSession.setPdfHighlights(all);
  },
  removePdfHighlight(id: string): void {
    const all = claimSession.getPdfHighlights().filter((h) => h.id !== id);
    claimSession.setPdfHighlights(all);
  },

  // Annotated PDF attachments (Packet step)
  getAnnotatedPdfAttachments(): AnnotatedPdfAttachment[] {
    return readJson<AnnotatedPdfAttachment[]>(K.annotatedPdfAttachments, []);
  },
  setAnnotatedPdfAttachments(attachments: AnnotatedPdfAttachment[]): void {
    writeJson(K.annotatedPdfAttachments, attachments);
  },
  upsertAnnotatedPdfAttachment(attachment: AnnotatedPdfAttachment): void {
    const all = claimSession.getAnnotatedPdfAttachments();
    const idx = all.findIndex((a) => a.id === attachment.id);
    const next = [...all];
    if (idx >= 0) next[idx] = attachment;
    else next.unshift(attachment);
    claimSession.setAnnotatedPdfAttachments(next);
  },
  removeAnnotatedPdfAttachment(id: string): void {
    const next = claimSession.getAnnotatedPdfAttachments().filter((a) => a.id !== id);
    claimSession.setAnnotatedPdfAttachments(next);
  },

  // Incurred receipts (Packet step)
  getIncurredReceipts(): IncurredReceiptAttachment[] {
    return readJson<IncurredReceiptAttachment[]>(K.incurredReceipts, []);
  },
  setIncurredReceipts(receipts: IncurredReceiptAttachment[]): void {
    writeJson(K.incurredReceipts, receipts);
  },
  upsertIncurredReceipt(receipt: IncurredReceiptAttachment): void {
    const all = claimSession.getIncurredReceipts();
    const idx = all.findIndex((r) => r.id === receipt.id);
    const next = [...all];
    if (idx >= 0) next[idx] = receipt;
    else next.unshift(receipt);
    claimSession.setIncurredReceipts(next);
  },
  removeIncurredReceipt(id: string): void {
    const next = claimSession.getIncurredReceipts().filter((r) => r.id !== id);
    claimSession.setIncurredReceipts(next);
  },

  // Trade recap variance PDF (Recap Variance step)
  getTradeRecapPdfAttachment(): TradeRecapPdfAttachment | null {
    return readJson<TradeRecapPdfAttachment | null>(K.tradeRecapPdf, null);
  },
  setTradeRecapPdfAttachment(attachment: TradeRecapPdfAttachment | null): void {
    if (!attachment) removeKey(K.tradeRecapPdf);
    else writeJson(K.tradeRecapPdf, attachment);
  },

  // Navigation helpers
  getLastRecapVariancePath(): string | null {
    return readJson<string | null>(K.lastRecapVariancePath, null);
  },
  setLastRecapVariancePath(path: string | null): void {
    if (!path) removeKey(K.lastRecapVariancePath);
    else writeJson(K.lastRecapVariancePath, path);
  },

  // Utility
  clearAll(): void {
    Object.values(K).forEach((k) => removeKey(k));
  },
};
